import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { X, Smartphone, Monitor, RotateCcw, QrCode, Moon, Sun, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'@/shared/components/ui/select';
import { Badge } from '@/shared/components/ui/badge';

// Device presets
const DEVICES = {
  'iphone-14': { name: 'iPhone 14', width: 390, height: 844, notch: true },
  'iphone-14-pro-max': { name: 'iPhone 14 Pro Max', width: 430, height: 932, notch: true },
  'samsung-s23': { name: 'Samsung S23', width: 360, height: 780, notch: false },
  'pixel-7': { name: 'Pixel 7', width: 412, height: 915, notch: false },
  'generic': { name: 'Generic (375x812)', width: 375, height: 812, notch: false }
};

// App options
const APPS = {
  'driver': { name: 'Driver App', path: '/driver', color: '#E53935' },
  'warehouse': { name: 'Warehouse App', path: '/mobile', color: '#2563eb' }
};

export const MobilePreviewPanel = ({ isOpen, onClose }) => {const { t } = useTranslation("common");
  const [selectedDevice, setSelectedDevice] = useState('iphone-14');
  const [selectedApp, setSelectedApp] = useState('driver');
  const [isLandscape, setIsLandscape] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);

  const device = DEVICES[selectedDevice];
  const app = APPS[selectedApp];

  // Calculate dimensions based on orientation
  const frameWidth = isLandscape ? device.height : device.width;
  const frameHeight = isLandscape ? device.width : device.height;

  // Scale to fit container (max 70% viewport height)
  const maxHeight = window.innerHeight * 0.7;
  const scale = frameHeight > maxHeight ? maxHeight / frameHeight : 1;

  const refreshPreview = () => {
    setIframeKey((prev) => prev + 1);
  };

  // Build preview URL with theme parameter
  const previewUrl = `${app.path}?preview=true&theme=${isDarkMode ? 'dark' : 'light'}`;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose} />
      

      {/* Panel */}
      <div className="relative ml-auto w-full max-w-lg bg-card border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <Smartphone className="h-5 w-5 text-primary" />
            <h2 className="font-semibold text-lg">{t("mobilePreview", "Mobile Preview")}</h2>
            <Badge variant="outline" className="text-xs">{t("beta", "Beta")}</Badge>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Controls */}
        <div className="p-4 border-b border-border space-y-4">
          {/* App Selector */}
          <div className="flex gap-2">
            {Object.entries(APPS).map(([key, appInfo]) =>
            <Button
              key={key}
              variant={selectedApp === key ? 'default' : 'outline'}
              size="sm"
              className="flex-1"
              style={selectedApp === key ? { backgroundColor: appInfo.color } : {}}
              onClick={() => setSelectedApp(key)}>
              
                {appInfo.name}
              </Button>
            )}
          </div>

          {/* Device & Options Row */}
          <div className="flex items-center gap-3">
            <Select value={selectedDevice} onValueChange={setSelectedDevice}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Select device" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(DEVICES).map(([key, dev]) =>
                <SelectItem key={key} value={key}>
                    {dev.name} ({dev.width}×{dev.height})
                  </SelectItem>
                )}
              </SelectContent>
            </Select>

            {/* Rotate */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsLandscape(!isLandscape)}
              title="Rotate device">
              
              <RotateCcw className={cn("h-4 w-4 transition-transform", isLandscape && "rotate-90")} />
            </Button>

            {/* Theme Toggle */}
            <Button
              variant="outline"
              size="icon"
              onClick={() => setIsDarkMode(!isDarkMode)}
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
              
              {isDarkMode ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>

            {/* Refresh */}
            <Button
              variant="outline"
              size="icon"
              onClick={refreshPreview}
              title="Refresh preview">
              
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Device Frame */}
        <div className="flex-1 flex items-center justify-center p-6 bg-muted/30 overflow-hidden">
          <div
            className="relative transition-all duration-300"
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'center center'
            }}>
            
            {/* Phone Frame */}
            <div
              className={cn(
                "relative rounded-[3rem] border-[14px] border-gray-900 bg-gray-900 shadow-2xl overflow-hidden",
                "before:absolute before:inset-0 before:rounded-[2.5rem] before:border before:border-gray-700 before:pointer-events-none"
              )}
              style={{
                width: frameWidth + 28, // Add border width
                height: frameHeight + 28
              }}>
              
              {/* Notch (for iPhone) */}
              {device.notch && !isLandscape &&
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[120px] h-[30px] bg-gray-900 rounded-b-2xl z-10" />
              }

              {/* Status Bar Overlay */}
              <div
                className={cn(
                  "absolute top-0 left-0 right-0 h-[44px] z-10 flex items-center justify-between px-6 text-xs font-medium",
                  isDarkMode ? "text-white" : "text-black"
                )}
                style={{
                  background: isDarkMode ?
                  'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)' :
                  'linear-gradient(to bottom, rgba(255,255,255,0.9), transparent)'
                }}>
                
                <span>{t("941", "9:41")}</span>
                <div className="flex items-center gap-1">
                  <span>{t("5g", "5G")}</span>
                  <div className="flex gap-0.5">
                    <div className={cn("w-1 h-2 rounded-sm", isDarkMode ? "bg-white" : "bg-black")} />
                    <div className={cn("w-1 h-3 rounded-sm", isDarkMode ? "bg-white" : "bg-black")} />
                    <div className={cn("w-1 h-4 rounded-sm", isDarkMode ? "bg-white" : "bg-black")} />
                    <div className={cn("w-1 h-3 rounded-sm", isDarkMode ? "bg-white/50" : "bg-black/50")} />
                  </div>
                  <span>{t("100", "100%")}</span>
                </div>
              </div>

              {/* App Content (iframe) */}
              <iframe
                key={iframeKey}
                src={previewUrl}
                className="w-full h-full border-0 rounded-[2.5rem]"
                style={{
                  width: frameWidth,
                  height: frameHeight,
                  backgroundColor: isDarkMode ? '#0D0D0D' : '#FAFAFA'
                }}
                title="Mobile Preview" />
              

              {/* Home Indicator (for iPhone) */}
              {device.notch && !isLandscape &&
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[134px] h-[5px] bg-gray-600 rounded-full" />
              }
            </div>
          </div>
        </div>

        {/* Footer Info */}
        <div className="p-4 border-t border-border bg-muted/30">
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: app.color }} />
              
              <span>{app.name}</span>
              <span>•</span>
              <span>{device.name}</span>
              <span>•</span>
              <span>{frameWidth}×{frameHeight}</span>
            </div>
            <Button variant="ghost" size="sm" className="gap-2">
              <QrCode className="h-4 w-4" />{t("qrCode", "QR Code")}

            </Button>
          </div>
        </div>
      </div>
    </div>);

};

export default MobilePreviewPanel;